import { InTheWildPlate, type WildPlate } from './InTheWildPlate';

/**
 * Four projects in the composition from Figma 149:13331 / 149:13347 — two
 * rows, each a pair of unequal plates, the second of each pair dropped down
 * the page so no two plate tops line up.
 *
 * Columns are `fr` in the design's own proportions (1080:580, 280:620:700 with
 * a 60px gutter) so the whole run keeps its geometry at any container width,
 * and the heights come from an aspect per position rather than fixed pixels.
 *
 * The run repeats for every four projects, which is what makes it a pattern
 * and not a one-off layout — the drop distances repeat with it.
 */
const ROW_GAP = 'md:gap-[3.75rem]';

export const InTheWildRun = ({
    plates,
}: {
    /** Up to four projects; a short final run simply draws fewer plates. */
    plates: WildPlate[];
    /** The number the first plate of this run carries. */
}) => {
    const [one, two, three, four] = plates;

    return (
        <div className="flex flex-col max-md:gap-10 md:gap-[5.625rem]">
            {one ? (
                <div
                    className={`grid max-md:grid-cols-1 max-md:gap-10 md:grid-cols-[1080fr_580fr] ${ROW_GAP}`}
                >
                    <InTheWildPlate
                        plate={one}
                        aspectClass="max-md:aspect-[4/3] md:aspect-[1080/700]"
                    />
                    {two ? (
                        // Dropped 180px so its top sits below the first plate's.
                        <div className="md:pt-[11.25rem]">
                            <InTheWildPlate
                                plate={two}
                                aspectClass="max-md:aspect-[4/3] md:aspect-[580/780]"
                            />
                        </div>
                    ) : null}
                </div>
            ) : null}

            {three ? (
                <div
                    className={`grid max-md:grid-cols-1 max-md:gap-10 md:grid-cols-[280fr_620fr_700fr] ${ROW_GAP}`}
                >
                    {/* An empty first column — the row is indented, not
                        centred, so the pair sits off the left margin. */}
                    <div aria-hidden="true" className="max-md:hidden" />
                    <InTheWildPlate
                        plate={three}
                        aspectClass="max-md:aspect-[4/3] md:aspect-[620/460]"
                    />
                    {four ? (
                        <div className="md:pt-[8.75rem]">
                            <InTheWildPlate
                                plate={four}
                                aspectClass="max-md:aspect-[4/3] md:aspect-[700/540]"
                            />
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};
